<template>
  <div class="activityPageEdit">
    <el-breadcrumb separator-class="el-icon-arrow-right">
      <el-breadcrumb-item>内容</el-breadcrumb-item>
      <el-breadcrumb-item :to="{ name: 'activityPageList' }">自定义活动页</el-breadcrumb-item>
      <el-breadcrumb-item>{{ uuid ? '编辑活动页' : '新建活动页' }}</el-breadcrumb-item>
    </el-breadcrumb>

    <div class="builder" v-loading="loading">
      <!-- 左：组件库 + 已加组件 -->
      <div class="pane pane-left">
        <div class="pane-title">组件库</div>
        <!--
          固定按钮，不做"拖拽到画布"。仓里 9 处 draggable 全是扁平列表内重排、没有
          跨容器 drop，做拖入画布等于从零搭一个装修器；而点按钮追加 + 列表内拖动排序
          已经够用。后端的 activity_page_component 表留好了类型这一层，加组件不改表。
        -->
        <el-button
          v-for="t in componentTypes"
          :key="t.type"
          class="add-component"
          icon="el-icon-plus"
          @click="addComponent(t.type)"
        >
          {{ t.label }}
        </el-button>

        <div class="pane-title mg-t-20">页面结构</div>
        <p v-if="!components.length" class="font-12 color-999">还没有组件，点上面的按钮加一个。</p>
        <draggable v-model="components" handle=".drag-handle">
          <div
            v-for="(component, index) in components"
            :key="component._k"
            class="component-item"
            :class="{ on: activeIndex === index }"
            @click="selectComponent(index)"
          >
            <i class="el-icon-rank drag-handle"></i>
            <span>{{ componentSummary(component) }}</span>
            <i class="el-icon-delete" @click.stop="removeComponent(index)"></i>
          </div>
        </draggable>
      </div>

      <!-- 中：手机预览。就是把图按 750 宽等比铺下来，所见即所得 -->
      <div class="pane pane-center">
        <div class="phone">
          <div class="phone-bar">{{ pageForm.title || '页面标题' }}</div>
          <!--
            预览按四种组件各自的版式渲染，样式与小程序的
            components/activity-page-body 对齐（那边是 rpx，这里按 375 宽折半换算）。
            两边一起改，别只改一边 —— 预览骗人比没有预览更糟。
          -->
          <div class="phone-body">
            <div
              v-for="(component, ci) in components"
              :key="component._k"
              class="preview-block"
              :class="{ on: activeIndex === ci }"
              @click="selectComponent(ci)"
            >
              <!-- 1 图片广告 -->
              <template v-if="component.type === 1">
                <div v-for="image in component.images" :key="image._k" :class="component.layout === 2 ? 'pv-card' : 'pv-poster'">
                  <img :src="image.image" />
                  <div v-if="component.layout === 2 && image.caption" class="pv-card-title">{{ image.caption }}</div>
                </div>
              </template>
              <!-- 2 富文本 -->
              <div v-else-if="component.type === 2" class="pv-section pv-rich" v-html="component.content"></div>
              <!-- 3 栏目标题 -->
              <div v-else-if="component.type === 3" class="pv-section">
                <div v-if="component.layout === 2" class="pv-big">
                  <div class="pv-big-title">{{ component.title }}</div>
                  <div v-if="component.subtitle" class="pv-big-sub">{{ component.subtitle }}</div>
                </div>
                <div v-else class="pv-head">
                  <span class="pv-head-bar"></span>
                  <span>
                    <div class="pv-head-cn">{{ component.title }}</div>
                    <div v-if="component.subtitle" class="pv-head-en">{{ component.subtitle }}</div>
                  </span>
                </div>
              </div>
              <!-- 4 正文段落 -->
              <div v-else-if="component.type === 4" class="pv-section pv-body">
                <div class="pv-para" v-for="(para, pi) in splitParas(component.content)" :key="pi">{{ para }}</div>
              </div>
            </div>
            <p v-if="!components.length" class="empty">预览区：左侧加了组件就会显示在这里</p>
          </div>
        </div>
      </div>

      <!-- 右：页面设置 / 组件设置 -->
      <div class="pane pane-right">
        <el-tabs v-model="activeTab">
          <el-tab-pane label="页面设置" name="page">
            <el-form :model="pageForm" label-width="100px" size="small">
              <el-form-item label="页面标题" required>
                <el-input v-model="pageForm.title" maxlength="100" show-word-limit placeholder="也是小程序的标题栏文案"></el-input>
              </el-form-item>
              <el-form-item label="分享标题">
                <el-input v-model="pageForm.share_title" maxlength="100" placeholder="留空则与页面标题一致"></el-input>
              </el-form-item>
              <el-form-item label="分享描述">
                <el-input v-model="pageForm.share_desc" maxlength="255"></el-input>
              </el-form-item>
              <el-form-item label="分享海报">
                <uploadImageBtn @getImageUrl="url => (pageForm.share_image = url)" />
                <div v-if="pageForm.share_image" class="share-image">
                  <img :src="pageForm.share_image" />
                  <i class="el-icon-delete" @click="pageForm.share_image = ''"></i>
                </div>
                <p class="font-12 color-999">建议 4:3</p>
              </el-form-item>
              <el-form-item label="上架设置" required>
                <el-radio v-model="pageForm.shelf_type" :label="1">立即上架</el-radio>
                <el-radio v-model="pageForm.shelf_type" :label="2">定时上架</el-radio>
                <!--
                  ⚠ 时间选择器要单独占一行。它是 inline-block，跟两个单选挤在同一行时
                    只靠 margin-top 往下压，基线是歪的，右边还会被面板挤掉一截。
                    包一层 block 的 div 让它换行，宽度撑满。
                -->
                <div v-if="pageForm.shelf_type == 2" class="shelf-range">
                  <el-date-picker
                    v-model="shelfRange"
                    type="datetimerange"
                    range-separator="至"
                    start-placeholder="开始时间"
                    end-placeholder="结束时间"
                    value-format="yyyy-MM-dd HH:mm:ss"
                  >
                  </el-date-picker>
                </div>
                <p v-if="pageForm.shelf_type == 2" class="font-12 color-999 shelf-tip">
                  定时上架是「生效窗口」：窗口外小程序打开就是已下架。没有定时任务去翻状态。
                </p>
              </el-form-item>
              <el-form-item label="排序">
                <el-input-number v-model="pageForm.sort" :min="0" :step="1"></el-input-number>
                <span class="font-12 color-999 mg-l-10">数字小的在前</span>
              </el-form-item>
            </el-form>
          </el-tab-pane>

          <el-tab-pane label="组件设置" name="component">
            <p v-if="!currentComponent" class="font-12 color-999">先在左边选一个组件。</p>
            <template v-else>
              <el-form label-width="100px" size="small">
                <el-form-item label="组件类型">
                  <span>{{ typeLabel(currentComponent.type) }}</span>
                </el-form-item>
                <!-- 版式只有图片广告和栏目标题有，且两者的选项含义不同 -->
                <el-form-item v-if="layoutOptions(currentComponent.type).length > 1" label="版式">
                  <el-radio-group v-model="currentComponent.layout">
                    <el-radio v-for="o in layoutOptions(currentComponent.type)" :key="o.value" :label="o.value">
                      {{ o.label }}
                    </el-radio>
                  </el-radio-group>
                </el-form-item>

                <!-- 3 栏目标题 -->
                <template v-if="currentComponent.type === 3">
                  <el-form-item label="主标题" required>
                    <el-input v-model="currentComponent.title" maxlength="100" placeholder="例：博雅生命"></el-input>
                  </el-form-item>
                  <el-form-item label="副标题">
                    <el-input v-model="currentComponent.subtitle" maxlength="100" placeholder="例：About Boyalife，可留空"></el-input>
                  </el-form-item>
                </template>

                <!-- 4 正文段落 -->
                <el-form-item v-else-if="currentComponent.type === 4" label="正文" required>
                  <el-input
                    v-model="currentComponent.content"
                    type="textarea"
                    :rows="8"
                    placeholder="一行一段，空行会被忽略"
                  ></el-input>
                  <p class="font-12 color-999">按换行分段，样式与老落地页的正文一致，不用自己调</p>
                </el-form-item>

                <!-- 2 富文本 -->
                <el-form-item v-else-if="currentComponent.type === 2" label="富文本" required>
                  <!--
                    ⚠ 必须 :enueditor="false"，走 wangEditor 分支，不要用默认的 UEditor。
                      UEditor 的实例是**全局缓存**的（ueditor.all.js:20008）：
                      UE.getEditor 命中缓存就直接返回、不会 render 到新 DOM 上，而
                      render 是 appendChild 不清空容器。这个搭建器会反复切换组件、
                      同一个编辑器要反复挂载/销毁，于是切回来时拿到的是 DOM 已被摘掉
                      的旧实例 —— **编辑框一片空白，没法编辑**（实测过）。
                      试过在 uEditorTool 里加 beforeDestroy 调 UE.delEditor 兜，不行：
                      delEditor 是 `editor.destroy(); delete instances[id]`，destroy
                      抛异常就走不到那句 delete，缓存照样留着。而那是 19 个页面共用的
                      组件，不该为这一个页面去赌它的内部行为。

                      wangEditor 分支是 v-model="htmlx"，响应式、没有全局缓存、
                      不存在 id 冲突；而且它在那 19 个页面上一直被渲染着（只是 v-show
                      隐藏），等于生产环境天天在跑，不是没验证过的路径。

                    :key 保留 —— 富文本 A 切到富文本 B 时 v-else-if 仍为真、Vue 会
                    就地复用实例，加了 key 才会重建、内容不串。
                  -->
                  <wangEditorTool
                    ref="richEditor"
                    :key="currentComponent._k"
                    :enueditor="false"
                    :content="currentComponent.content"
                  />
                  <p class="font-12 color-999">排版自由，但效果取决于你在编辑器里怎么排；要和老落地页一致就用「栏目标题 + 正文段落」</p>
                </el-form-item>

                <!-- 1 图片广告 -->
                <el-form-item v-else label="添加图片">
                  <uploadImagesBtn @getImageUrl="onUploadImage" />
                  <el-button type="text" @click="imageDialogVisible = true">从素材库选择</el-button>
                  <p class="font-12 color-999">建议宽 750，拖拽可调整顺序</p>
                </el-form-item>
              </el-form>

              <draggable v-if="currentComponent.type === 1" v-model="currentComponent.images" handle=".drag-handle">
                <!--
                  ⚠ key 用的是每行自己的 _k，不是下标。用下标的话拖动排序或删掉中间
                    一张图之后，Vue 会按下标就地复用组件实例，富文本编辑器里显示的
                    还是上一行的内容。
                -->
                <div v-for="image in currentComponent.images" :key="image._k" class="image-row">
                  <div class="image-row-head">
                    <i class="el-icon-rank drag-handle"></i>
                    <img :src="image.image" class="thumb" />
                    <span class="flex-1 font-12 color-999">
                      {{ image.url_type ? '已配跳转' : '不跳转' }}
                    </span>
                    <i class="el-icon-delete" @click="removeImage(image)"></i>
                  </div>
                  <el-form label-width="100px" size="small" class="mg-t-10">
                    <!-- 说明文字只有「图+下方说明文字」这个版式才展示，别的版式填了也看不见 -->
                    <el-form-item v-if="currentComponent.layout === 2" label="说明文字">
                      <el-input v-model="image.caption" maxlength="100" placeholder="显示在图片下方"></el-input>
                    </el-form-item>
                    <jumpConfig ref="jumpConfigs" :row="image" :self-page-uuid="uuid" />
                  </el-form>
                </div>
              </draggable>
            </template>
          </el-tab-pane>
        </el-tabs>
      </div>
    </div>

    <div class="operateBtn">
      <el-button size="medium" type="primary" :loading="saving" @click="submit">保存</el-button>
      <el-button size="medium" @click="$router.go(-1)">返回</el-button>
    </div>

    <el-dialog title="选择图片" :visible.sync="imageDialogVisible" width="80%" :close-on-click-modal="false">
      <imageDialog @closeImageDialog="imageDialogVisible = false" @confirmImageDialog="onPickImages" />
    </el-dialog>
  </div>
</template>

<script>
  import draggable from 'vuedraggable'
  import imageDialog from '../../../components/imageDialog.vue'
  import uploadImageBtn from '../../../components/uploadImageBtn.vue'
  import uploadImagesBtn from '../../../components/uploadImagesBtn.vue'
  import jumpConfig from '../../../components/jumpConfig.vue'
  import wangEditorTool from '../../../components/wangEditorTool.vue'
  import { emptyRow } from '../../../components/jumpRow'

  /**
   * 组件类型。编号必须与后端 ActivityPageComponent::TYPE_* 一致。
   * ⚠ layout 在不同类型下含义不同（图片广告是图片排列、栏目标题是标题样式），
   *   所以按类型各给一份选项，不要合成一张全局表 —— 那样「栏目标题配了个
   *   单行一海报」这种自相矛盾的组合在界面上就选得出来。
   */
  const COMPONENT_TYPES = [
    { type: 1, label: '图片广告', layouts: [{ value: 1, label: '单行一海报' }, { value: 2, label: '图 + 下方说明文字' }] },
    { type: 2, label: '富文本', layouts: [{ value: 1, label: '默认' }] },
    { type: 3, label: '栏目标题', layouts: [{ value: 1, label: '橙色竖条式' }, { value: 2, label: '大字式' }] },
    { type: 4, label: '正文段落', layouts: [{ value: 1, label: '默认' }] }
  ]

  export default {
    name: 'activityPageEdit',
    components: { draggable, imageDialog, uploadImageBtn, uploadImagesBtn, jumpConfig, wangEditorTool },
    data() {
      return {
        uuid: '',
        loading: false,
        saving: false,
        activeTab: 'page',
        activeIndex: -1,
        imageDialogVisible: false,
        componentTypes: COMPONENT_TYPES,
        shelfRange: [],
        pageForm: {
          title: '',
          share_title: '',
          share_desc: '',
          share_image: '',
          shelf_type: 1,
          shelf_start_time: '',
          shelf_end_time: '',
          sort: 0
        },
        components: []
      }
    },
    computed: {
      currentComponent() {
        return this.components[this.activeIndex] || null
      }
    },
    created() {
      this.uuid = this.$route.query.uuid || ''
      if (this.uuid) this.getDetail()
    },
    methods: {
      getDetail() {
        this.loading = true
        this.$api.activityPage
          .getDetail(this.uuid)
          .then(res => {
            Object.keys(this.pageForm).forEach(key => {
              if (res[key] !== undefined && res[key] !== null) this.pageForm[key] = res[key]
            })
            // ⚠ 数值字段必须归一成 Number。后端开了 PDO::ATTR_EMULATE_PREPARES，
            //   所有列以**字符串**出线，shelf_type 回来是 "1"；而 el-radio 的
            //   :label 绑的是数字、内部用 === 严格比较 —— 两个单选一个都不会亮，
            //   运营看到「上架设置」是空的（新建时不会复现，初值本来就是数字，
            //   只有编辑已有页面才踩）。
            //   sort 同理：el-input-number 拿到字符串，加减按钮的行为会不对。
            this.pageForm.shelf_type = Number(this.pageForm.shelf_type) || 1
            this.pageForm.sort = Number(this.pageForm.sort) || 0
            if (this.pageForm.shelf_type === 2) {
              this.shelfRange = [this.pageForm.shelf_start_time, this.pageForm.shelf_end_time]
            }
            // 把后端返回的图补齐成"所有键都在"的形状 —— Vue 2 对后加的属性不做响应式，
            // 少一个键，那个字段在跳转配置里就是改了不刷新。
            // ⚠ 后端所有列以字符串出线（PDO::ATTR_EMULATE_PREPARES），
            //   type/layout 回来是 "1" 这种字符串。模板里到处是 === 1 的比较，
            //   不 Number() 的话每个 v-if 分支都不成立，界面一片空白。
            this.components = (res.components || []).map(c => ({
              _k: nextKey(),
              type: Number(c.type) || 1,
              layout: Number(c.layout) || 1,
              title: c.title || '',
              subtitle: c.subtitle || '',
              content: c.content || '',
              images: (c.images || []).map(img =>
                Object.assign(emptyRow(), { _k: nextKey(), image: img.image, caption: img.caption || '' }, pickJump(img))
              )
            }))
            if (this.components.length) this.activeIndex = 0
          })
          .catch(err => this.$message.error(err))
          .finally(() => {
            this.loading = false
          })
      },

      /**
       * 收一遍当前挂着的 jumpConfig 的富文本内容。
       * $refs.jumpConfigs 只包含**当前选中组件**下的那几个（右侧面板是 v-else 渲染的），
       * 所以切组件之前必须先收一次 —— 否则在 A 组件写完富文本、点到 B 再保存，
       * A 那张图存回去的还是加载时的旧值，界面上还提示"保存成功"。
       */
      collectEditors() {
        const list = this.$refs.jumpConfigs
        if (list) {
          ;(Array.isArray(list) ? list : [list]).forEach(e => e.collect())
        }
        // 富文本组件自己那个编辑器（与图上的跳转富文本是两回事）。
        // ⚠ 同样要绕过 wangEditorTool.html 这个计算属性：它最终调的 getContent()
        //   不是响应式的，求值一次就被缓存，第二次保存会拿到第一次的内容。
        const tool = this.$refs.richEditor
        const editor = Array.isArray(tool) ? tool[0] : tool
        if (editor && this.currentComponent && Number(this.currentComponent.type) === 2) {
          // 走的是 wangEditor 分支（:enueditor="false"），`html` 计算属性直接返回
          // v-model 绑着的 htmlx —— **是响应式的**，每次读都是当前内容。
          // （UEditor 分支的 html 最终调 getContent()，那不是响应式的，求值一次就
          //   被缓存，第二次保存会拿到第一次的内容。这也是不用它的原因之一。）
          const html = editor.html
          // 空内容如实回写，让后端明确报「还没写内容」——
          // 写成"非空才回写"的话，运营删光了反而保存出旧内容，清空不掉。
          this.currentComponent.content = !html || html === '<p><br></p>' ? '' : html
        }
      },

      // 选中组件时顺手把右侧切到「组件设置」—— 不然点了左边看不出哪里变了
      selectComponent(index) {
        if (index !== this.activeIndex) this.collectEditors()
        this.activeIndex = index
        this.activeTab = 'component'
      },
      typeLabel(type) {
        const t = COMPONENT_TYPES.find(x => x.type === Number(type))
        return t ? t.label : '未知组件'
      },
      layoutOptions(type) {
        const t = COMPONENT_TYPES.find(x => x.type === Number(type))
        return t ? t.layouts : []
      },
      // 左侧「页面结构」里每一条的摘要，让运营不用点开就知道这块是什么
      componentSummary(c) {
        const label = this.typeLabel(c.type)
        if (Number(c.type) === 1) return `${label}（${(c.images || []).length}张）`
        if (Number(c.type) === 3) return `${label}：${c.title || '未填'}`
        const text = String(c.content || '').replace(/<[^>]+>/g, '').trim()
        return `${label}：${text ? text.slice(0, 10) : '未填'}`
      },
      splitParas(text) {
        return String(text || '')
          .split(/\r?\n/)
          .map(s => s.trim())
          .filter(s => s)
      },

      addComponent(type) {
        // 每种类型的字段都先建齐 —— Vue 2 对后加的属性不做响应式，
        // 少一个键那个输入框就是"填了不刷新"。
        this.components.push({
          _k: nextKey(),
          type: Number(type) || 1,
          layout: 1,
          title: '',
          subtitle: '',
          content: '',
          images: []
        })
        this.selectComponent(this.components.length - 1)
      },
      removeComponent(index) {
        this.components.splice(index, 1)
        if (this.activeIndex >= this.components.length) this.activeIndex = this.components.length - 1
      },
      onUploadImage(url) {
        this.pushImage(url)
      },
      onPickImages(list) {
        ;(list || []).forEach(i => this.pushImage(i.url))
        this.imageDialogVisible = false
      },
      pushImage(url) {
        if (!this.currentComponent) return this.$message.warning('先在左边选一个组件')
        this.currentComponent.images.push(Object.assign(emptyRow(), { _k: nextKey(), image: url }))
      },
      removeImage(image) {
        const index = this.currentComponent.images.indexOf(image)
        if (index !== -1) this.currentComponent.images.splice(index, 1)
      },

      submit() {
        // ⚠ 富文本是 ref 取值的，不 collect 就会把运营刚写的内容丢掉存成空。
        this.collectEditors()

        if (!this.pageForm.title) return this.$message.warning('请填写页面标题')
        if (this.pageForm.shelf_type == 2) {
          if (!this.shelfRange || this.shelfRange.length !== 2) return this.$message.warning('请选择定时上架的时间范围')
          this.pageForm.shelf_start_time = this.shelfRange[0]
          this.pageForm.shelf_end_time = this.shelfRange[1]
        } else {
          this.pageForm.shelf_start_time = ''
          this.pageForm.shelf_end_time = ''
        }

        const payload = Object.assign({}, this.pageForm, {
          // sort 由数组顺序决定，跟左侧和图片列表里拖出来的顺序一致
          components: this.components.map((c, ci) => ({
            type: c.type,
            layout: c.layout,
            sort: ci,
            title: c.title,
            subtitle: c.subtitle,
            content: c.content,
            // 只有图片广告才带图；后端 saveComponents 对别的类型也会丢掉 images，
            // 这里先不发是为了省掉一次无谓的传输
            images: Number(c.type) === 1 ? c.images.map((img, ii) => Object.assign({}, img, { sort: ii })) : []
          }))
        })

        this.saving = true
        const request = this.uuid
          ? this.$api.activityPage.update(this.uuid, payload)
          : this.$api.activityPage.create(payload)
        request
          .then(res => {
            this.$message.success('保存成功')
            // 新建完直接留在编辑页并挂上 uuid，否则再点一次保存会建出第二个页面
            if (!this.uuid && res && res.uuid) {
              this.uuid = res.uuid
              this.$router.replace({ name: 'activityPageEdit', query: { uuid: res.uuid } })
            }
          })
          .catch(err => this.$message.error(err))
          .finally(() => {
            this.saving = false
          })
      }
    }
  }

  /**
   * 给组件和图发一个本地唯一 key，只在前端用（v-for 的 :key）。
   * 用下标当 key 的话，拖拽排序或删掉中间一行之后 Vue 会就地复用组件实例，
   * 富文本编辑器里会显示上一行的内容。
   * 后端 saveComponents 走白名单，_k 提交上去会被丢掉。
   */
  let keySeed = 0
  function nextKey() {
    keySeed += 1
    return 'k' + keySeed
  }

  /**
   * 只挑跳转相关的键，别把后端返回的 uuid / create_time / url_type_name 之类
   * 一起塞回 row —— 那些提交回去会被后端白名单挡掉，但留在本地会让"已配跳转"
   * 的判断变糊涂。
   *
   * ⚠ url 特意**不取**后台详情里的派生值：后端 cmsDetail 传的是运营原样填的值
   * （loadComponents 的 derive_url=false），这里取的就是原值。
   */
  function pickJump(img) {
    const keys = Object.keys(emptyRow())
    const out = {}
    keys.forEach(key => {
      if (img[key] !== undefined) out[key] = img[key]
    })
    return out
  }
</script>

<style scoped>
  .builder {
    display: flex;
    align-items: flex-start;
    margin-top: 20px;
  }
  .pane {
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 0 5px rgba(0, 0, 0, 0.1);
    padding: 16px;
  }
  .pane-left {
    width: 220px;
    flex: none;
  }
  .pane-center {
    flex: none;
    width: 415px;
    margin: 0 16px;
  }
  .pane-right {
    flex: 1;
    min-width: 0;
  }
  .pane-title {
    font-weight: 600;
    margin-bottom: 10px;
  }
  .add-component {
    width: 100%;
    margin-bottom: 8px;
  }
  /*
    ⚠ 这条不能省。Element 自带 `.el-button + .el-button { margin-left: 10px }`，
    会把第 2 个之后的按钮整体顶右 10px；而这些按钮是 width:100%，于是右边溢出，
    看起来就是"第一个按钮和后三个没对齐"。
  */
  .add-component + .add-component {
    margin-left: 0;
  }
  /* 时间选择器单独一行，撑满配置面板的宽度 */
  .shelf-range {
    margin-top: 10px;
  }
  .shelf-range >>> .el-date-editor {
    width: 100%;
  }
  /* 提示语紧跟在选择器下面，去掉 p 的默认外边距 */
  .shelf-tip {
    margin: 6px 0 0;
    line-height: 1.5;
  }
  .component-item {
    display: flex;
    align-items: center;
    padding: 8px 10px;
    margin-bottom: 8px;
    border: 1px solid #ebeef5;
    border-radius: 4px;
    cursor: pointer;
    font-size: 13px;
  }
  .component-item.on {
    border-color: #409eff;
    background: #ecf5ff;
  }
  .component-item span {
    flex: 1;
    margin: 0 8px;
  }
  .drag-handle {
    cursor: move;
    color: #909399;
  }
  .phone {
    width: 375px;
    margin: 0 auto;
    border: 1px solid #dcdfe6;
    border-radius: 8px;
    overflow: hidden;
  }
  .phone-bar {
    height: 44px;
    line-height: 44px;
    text-align: center;
    background: #f7f8fa;
    border-bottom: 1px solid #ebeef5;
    font-size: 14px;
  }
  .phone-body {
    min-height: 500px;
    max-height: 640px;
    overflow-y: auto;
    background: #fff;
  }
  .phone-body .empty {
    padding: 40px 20px;
    text-align: center;
    color: #999;
    font-size: 12px;
  }
  /*
    预览区版式：与小程序 components/activity-page-body 一一对应。
    那边是 750 设计稿的 rpx，这里预览宽 375px，所以数值都是它的一半。
    ⚠ 两边一起改 —— 预览和真机不一致，比没有预览更容易让运营配错。
  */
  .preview-block {
    cursor: pointer;
    border: 2px solid transparent;
  }
  .preview-block.on {
    border-color: #409eff;
  }
  .pv-poster {
    line-height: 0;
  }
  .pv-poster img {
    width: 100%;
    display: block;
  }
  .pv-section {
    padding: 24px 16px 0;
  }
  .pv-card {
    padding: 0 16px;
    margin-bottom: 16px;
  }
  .pv-card img {
    width: 100%;
    display: block;
    border-radius: 6px;
  }
  .pv-card-title {
    font-size: 15px;
    font-weight: 600;
    color: #353535;
    line-height: 21px;
    margin-top: 8px;
  }
  .pv-head {
    display: flex;
    align-items: center;
    margin-bottom: 14px;
  }
  .pv-head-bar {
    width: 4px;
    height: 24px;
    background-color: #f29700;
    border-radius: 2px;
    margin-right: 8px;
    flex-shrink: 0;
  }
  .pv-head-cn {
    font-size: 18px;
    font-weight: 600;
    color: #353535;
    line-height: 24px;
  }
  .pv-head-en {
    font-size: 12px;
    color: #999999;
    line-height: 17px;
  }
  .pv-big {
    margin-bottom: 12px;
  }
  .pv-big-title {
    font-size: 24px;
    font-weight: 700;
    color: #353535;
    line-height: 29px;
  }
  .pv-big-sub {
    font-size: 14px;
    color: #999999;
  }
  .pv-body {
    font-size: 13px;
    color: #666666;
    line-height: 22px;
  }
  .pv-para {
    margin-bottom: 10px;
  }
  .pv-rich >>> img {
    max-width: 100%;
  }
  /*
    wangEditorTool 的 wangEditor 分支在模板里写死了 width: 1067px（行内样式），
    会把右侧配置面板撑破、整个搭建器横向出滚动条。
    行内样式只能用 !important 覆盖；>>> 是为了穿透 scoped 打到子组件里。
  */
  >>> .box {
    width: 100% !important;
    max-width: 100%;
    box-sizing: border-box;
  }
  .image-row {
    border: 1px solid #ebeef5;
    border-radius: 4px;
    padding: 10px;
    margin-bottom: 12px;
  }
  .image-row-head {
    display: flex;
    align-items: center;
  }
  .image-row-head .thumb {
    width: 60px;
    height: 60px;
    object-fit: cover;
    margin: 0 10px;
  }
  .image-row-head .flex-1 {
    flex: 1;
  }
  .image-row-head .el-icon-delete {
    cursor: pointer;
    color: #f56c6c;
  }
  .share-image {
    position: relative;
    display: inline-block;
    margin-top: 10px;
  }
  .share-image img {
    width: 160px;
    display: block;
  }
  .share-image .el-icon-delete {
    position: absolute;
    right: 6px;
    top: 6px;
    color: #fff;
    cursor: pointer;
  }
  .operateBtn {
    margin-top: 20px;
    text-align: center;
  }
</style>
